import {
  type ConsentDocumentRow,
} from "@nexys/user-management-client";
import { AlertCircle, Trash2, Users } from "lucide-react";
import { useState, type FormEvent } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import { Label } from "../ui/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Skeleton } from "../ui/skeleton.js";
import { Switch } from "../ui/switch.js";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";
import { Textarea } from "../ui/textarea.js";

export interface ProjectConsentsProps {
  projectId: string;
  canManage?: boolean;
}

/** Newest version first within each key; keys alphabetical. */
const groupByKey = (docs: ConsentDocumentRow[]) => {
  const byKey = new Map<string, ConsentDocumentRow[]>();
  for (const d of docs) {
    const list = byKey.get(d.key) ?? [];
    list.push(d);
    byKey.set(d.key, list);
  }
  return [...byKey.entries()]
    .map(([key, versions]) => ({
      key,
      versions: [...versions].sort((a, b) => b.version - a.version),
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
};

export const ProjectConsents = ({
  projectId,
  canManage = false,
}: ProjectConsentsProps) => {
  const { data, loading, error, refresh } = useCall("listConsentDocuments", {
    projectId,
  });
  const groups = groupByKey(data?.documents ?? []);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{errorMessage(error)}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Agreements</CardTitle>
          <CardDescription>
            Terms, privacy notices and opt-ins users accept. Publishing new
            wording adds a version — the old text is kept so past acceptances
            still say what was agreed to.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : groups.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No agreements yet. Publish one to start collecting consent.
            </p>
          ) : (
            groups.map((g) => (
              <DocumentGroup
                key={g.key}
                projectId={projectId}
                docKey={g.key}
                versions={g.versions}
                canManage={canManage}
                onChanged={refresh}
              />
            ))
          )}
          {canManage ? (
            <PublishDialog
              projectId={projectId}
              existingKeys={groups.map((g) => g.key)}
              onPublished={refresh}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

const DocumentGroup = ({
  projectId,
  docKey,
  versions,
  canManage,
  onChanged,
}: {
  projectId: string;
  docKey: string;
  versions: ConsentDocumentRow[];
  canManage: boolean;
  onChanged: () => void;
}) => {
  const client = useUmClient();
  const [busy, setBusy] = useState<string | null>(null);
  // Groups are only built from non-empty version lists.
  const current = versions[0];
  if (!current) return null;

  const togglePublished = async (d: ConsentDocumentRow) => {
    setBusy(d.id);
    await client.updateConsentDocument({
      projectId,
      documentId: d.id,
      published: !d.published,
    });
    setBusy(null);
    onChanged();
  };

  const remove = async (d: ConsentDocumentRow) => {
    setBusy(d.id);
    await client.deleteConsentDocument({ projectId, documentId: d.id });
    setBusy(null);
    onChanged();
  };

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
        <div className="grid gap-0.5">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{current.title}</span>
            <Badge
              variant={
                current.enforcement === "blocking" ? "default" : "muted"
              }
            >
              {current.enforcement === "blocking"
                ? "blocks sign-in"
                : "report only"}
            </Badge>
          </div>
          <code className="text-muted-foreground font-mono text-xs">
            {docKey} · {versions.length} version
            {versions.length === 1 ? "" : "s"}
          </code>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Version</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Re-accept</TableHead>
            <TableHead className="text-right">Acceptances</TableHead>
            {canManage ? <TableHead /> : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map((d) => (
            <TableRow key={d.id}>
              <TableCell className="font-mono text-xs">v{d.version}</TableCell>
              <TableCell>
                {canManage ? (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={d.published}
                      onCheckedChange={() => void togglePublished(d)}
                      disabled={busy === d.id}
                      aria-label="Published"
                    />
                    <span className="text-muted-foreground text-xs">
                      {d.published ? "published" : "draft"}
                    </span>
                  </div>
                ) : (
                  <Badge variant={d.published ? "default" : "muted"}>
                    {d.published ? "published" : "draft"}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground text-xs">
                {d.requiresReacceptance ? "required" : "not required"}
              </TableCell>
              <TableCell className="text-right">
                <AcceptancesDialog projectId={projectId} document={d} />
              </TableCell>
              {canManage ? (
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    onClick={() => void remove(d)}
                    disabled={busy === d.id}
                    aria-label="Delete version"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TableCell>
              ) : null}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/// Who accepted one version — the compliance view.
const AcceptancesDialog = ({
  projectId,
  document,
}: {
  projectId: string;
  document: ConsentDocumentRow;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Users className="size-3.5" /> View
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {document.title} — v{document.version}
          </DialogTitle>
          <DialogDescription>
            Everyone who accepted this exact version.
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <AcceptancesTable projectId={projectId} documentId={document.id} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const AcceptancesTable = ({
  projectId,
  documentId,
}: {
  projectId: string;
  documentId: string;
}) => {
  const { data, loading, error } = useCall("listConsentAcceptances", {
    projectId,
    documentId,
  });

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle />
        <AlertDescription>{errorMessage(error)}</AlertDescription>
      </Alert>
    );
  }
  if (loading) {
    return <Skeleton className="h-24 w-full" />;
  }

  const rows = data?.acceptances ?? [];
  return (
    <div className="max-h-96 overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Accepted</TableHead>
            <TableHead>IP</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableEmpty>Nobody has accepted this version yet.</TableEmpty>
          ) : (
            rows.map((a) => (
              <TableRow key={a.userId}>
                <TableCell>
                  <div className="grid gap-0.5">
                    <span className="text-sm">{a.userName}</span>
                    <span className="text-muted-foreground text-xs">
                      {a.userEmail}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-xs">
                  {new Date(a.acceptedAt).toLocaleString()}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  {a.ipAddress ?? "—"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {data ? (
        <p className="text-muted-foreground mt-2 text-xs">
          {rows.length} of {data.total}
        </p>
      ) : null}
    </div>
  );
};

const PublishDialog = ({
  projectId,
  existingKeys,
  onPublished,
}: {
  projectId: string;
  existingKeys: string[];
  onPublished: () => void;
}) => {
  const client = useUmClient();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [enforcement, setEnforcement] = useState<"blocking" | "report_only">(
    "blocking"
  );
  const [requiresReacceptance, setRequiresReacceptance] = useState(true);
  const [published, setPublished] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isNewVersion = existingKeys.includes(key.trim().toLowerCase());

  const reset = () => {
    setKey("");
    setTitle("");
    setBody("");
    setUrl("");
    setEnforcement("blocking");
    setRequiresReacceptance(true);
    setPublished(true);
    setError(null);
    setLoading(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.publishConsentDocument({
      projectId,
      key: key.trim().toLowerCase(),
      title: title.trim(),
      body,
      url: url.trim() || null,
      enforcement,
      requiresReacceptance,
      published,
    });
    setLoading(false);
    if (res.ok) {
      onPublished();
      reset();
      setOpen(false);
    } else {
      setError(errorMessage(res.error, "Couldn't publish this agreement."));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Publish agreement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Publish an agreement</DialogTitle>
          <DialogDescription>
            Reusing an existing key publishes the next version of it. Text is
            never edited in place, so past acceptances stay meaningful.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Key"
              htmlFor="consent-key"
              description="Stable id across versions, e.g. terms."
              required
            >
              <Input
                id="consent-key"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="terms"
                className="font-mono"
                list="consent-key-options"
              />
              <datalist id="consent-key-options">
                {existingKeys.map((k) => (
                  <option key={k} value={k} />
                ))}
              </datalist>
            </Field>
            <Field label="Title" htmlFor="consent-title" required>
              <Input
                id="consent-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Terms of Service"
              />
            </Field>
          </div>
          {isNewVersion ? (
            <p className="text-muted-foreground text-xs">
              <strong className="font-mono">{key.trim().toLowerCase()}</strong>{" "}
              already exists — this publishes its next version.
            </p>
          ) : null}
          <Field label="Body" htmlFor="consent-body" required>
            <Textarea
              id="consent-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              className="text-xs"
            />
          </Field>
          <Field
            label="Link"
            htmlFor="consent-url"
            description="Optional link to the canonical hosted document."
          >
            <Input
              id="consent-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/terms"
              className="font-mono"
            />
          </Field>
          <Field
            label="Enforcement"
            htmlFor="consent-enforcement"
            description="Blocking withholds sign-in until the user accepts."
          >
            <Select
              value={enforcement}
              onValueChange={(v) =>
                setEnforcement(v as "blocking" | "report_only")
              }
            >
              <SelectTrigger id="consent-enforcement">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="blocking">Blocks sign-in</SelectItem>
                <SelectItem value="report_only">Report only</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-0.5">
              <Label htmlFor="consent-reaccept">Requires re-acceptance</Label>
              <p className="text-muted-foreground text-xs">
                On for a material change — everyone who accepted an earlier
                version is asked again. Off for a typo fix.
              </p>
            </div>
            <Switch
              id="consent-reaccept"
              checked={requiresReacceptance}
              onCheckedChange={setRequiresReacceptance}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-0.5">
              <Label htmlFor="consent-published">Publish now</Label>
              <p className="text-muted-foreground text-xs">
                Off saves it as a draft: never shown, never enforced.
              </p>
            </div>
            <Switch
              id="consent-published"
              checked={published}
              onCheckedChange={setPublished}
            />
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!key.trim() || !title.trim() || !body.trim()}
            >
              Publish
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
